import React, {useEffect, useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import AddIcon from '@mui/icons-material/Add';
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../components/grid/GridAction";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from '@mui/icons-material/Create';
import {t} from "../../../../store/localization/TranslMessasge";
import ExtGridWrapper from "../../../../components/grid/ExtGridWrapper";
import {CommonTranslation} from "../CommonTranslation";
import {PersistentStateId} from "../../../../store/common/Grid";
import {CoreDataTypeFormatter} from "../datatype/CoreDataTypeFormatter";
import {PropertyComparator} from "./PropertyComparator";
import PropertyCreateDialog, {PropertyCreateApi} from "./create/PropertyCreateDialog";
import PropertyUpdateDialog, {PropertyUpdateApi} from "./update/PropertyUpdateDialog";
import PropertyDeleteConfirmationDialog, {PropertyDeleteApi} from "./delete/PropertyDeleteConfirmationDialog";
import {PropertyDto} from "../../../../common/apis/property/PropertyApi";
import {PropertiesTranslationKey} from "./PropertiesTranslationKey";


export interface PropertyApi extends PropertyCreateApi, PropertyUpdateApi, PropertyDeleteApi {
    getProperties: () => Promise<PropertyDto[]>;
}

interface PropertiesPanelProps {
    canUpdateProperties: boolean;
    propertyApi: PropertyApi;
    persistentStateId: PersistentStateId;
    resourceId: string;
    refreshTrigger?: number;
}

export default function PropertiesPanel(props: Readonly<PropertiesPanelProps>) {
    const {canUpdateProperties, propertyApi, refreshTrigger} = props;
    const {persistentStateId, resourceId} = props;

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const [properties, setProperties] = useState<PropertyDto[] | undefined>(undefined);
    const [localRefreshTrigger, setLocalRefreshTrigger] = useState<number>(0);
    const [selectedProperty, setSelectedProperty] = useState<PropertyDto | undefined>(undefined);

    const effectiveRefreshTrigger = Math.max(localRefreshTrigger, refreshTrigger ?? 0);

    useEffect(() => {
        propertyApi.getProperties()
            .then((properties) => {
                setProperties(properties);
            });
    }, [propertyApi, resourceId, effectiveRefreshTrigger]);

    function refreshProperties() {
        setLocalRefreshTrigger(Date.now());
    }

    function openCreateDialog() {
        setCreateDialogOpen(true);
    }

    function closeCreateDialog() {
        setCreateDialogOpen(false);
    }

    function openUpdateDialog(property: PropertyDto) {
        setSelectedProperty(property);
        setUpdateDialogOpen(true);
    }

    function closeUpdateDialog() {
        setUpdateDialogOpen(false);
    }

    function openDeleteDialog(property: PropertyDto) {
        setSelectedProperty(property);
        setDeleteDialogOpen(true);
    }

    function closeDeleteDialog() {
        setDeleteDialogOpen(false);
    }

    const colDefs: GridColDef[] = [
        { field: 'name', headerName: t(PropertiesTranslationKey.NAME), width: 300},
        { field: 'value', headerName: t(PropertiesTranslationKey.VALUE), width: 300},
        { field: 'type', headerName: t(PropertiesTranslationKey.TYPE), width: 300, valueFormatter: (params) => CoreDataTypeFormatter.formatCoreDataType(params.value as string, t)},
    ];

    if (!properties) {
        return null;
    }

    properties.sort(PropertyComparator.compare);

    const rows = properties.map((prop, index) => ({
        identifier: prop.definition.identifier,
        name: prop.definition.name,
        type: prop.definition.type,
        value: prop.value,
        index: index,
    }));

    return (
        <>
            <ExtGridWrapper
                    columns={colDefs}
                    rows={rows}
                    rowCount={rows.length}
                    getRowId={row => row.identifier + row.index}
                    actions={[
                        GridAction.buttonBuilder("ADD_PROPERTY", ActionButtonType.IMMEDIATE, t(PropertiesTranslationKey.ADD_PROPERTY),
                            <AddIcon/>)
                            .enabledPolicy(EnabledPolicy.ALWAYS)
                            .isEnabled(() => canUpdateProperties)
                            .onClick(() => openCreateDialog())
                            .build(),
                        GridAction.buttonBuilder("UPDATE_PROPERTY", ActionButtonType.IMMEDIATE, t(PropertiesTranslationKey.EDIT_PROPERTY_VALUE),
                            <CreateIcon/>)
                            .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                            .isEnabled(() => canUpdateProperties)
                            .onClick((_, selectedRows) => {
                                const property = properties.filter(p => p.definition.identifier === selectedRows[0].identifier)[0];
                                if (property) {
                                    openUpdateDialog(property);
                                }
                            }).build(),
                        GridAction.buttonBuilder("DELETE_PROPERTY", ActionButtonType.IMMEDIATE, t(CommonTranslation.REMOVE),
                            <DeleteIcon/>)
                            .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                            .isEnabled(() => canUpdateProperties)
                            .onClick((selectedRowIds, selectedRows) => {
                                const property = properties.filter(p => p.definition.identifier === selectedRows[0].identifier)[0];
                                if (property) {
                                    openDeleteDialog(property);
                                }
                            }).build(),
                    ]}
                    peristentStateId={persistentStateId}
                    resourceId={resourceId} />

            <PropertyCreateDialog
                    open={createDialogOpen}
                    onCreated={refreshProperties}
                    onClosed={closeCreateDialog}
                    existingPropertyNames={new Set(properties.map(prop => prop.definition.name))}
                    propertyCreateApi={propertyApi} />

            {selectedProperty &&
                <PropertyUpdateDialog
                        open={updateDialogOpen}
                        property={selectedProperty}
                        onUpdated={refreshProperties}
                        onClosed={closeUpdateDialog}
                        propertyUpdateApi={propertyApi} />
            }

            {selectedProperty &&
                <PropertyDeleteConfirmationDialog
                    open={deleteDialogOpen}
                    propertyDefinitionId={selectedProperty.definition.identifier}
                    onDeleted={refreshProperties}
                    onClose={closeDeleteDialog}
                    propertyDeleteApi={propertyApi} />
            }
        </>
    );
}
